// Fix IC (2026-10-29): Inventario › «Apertura/Cierre de Almacenes y Depósitos» (CEN, Ing. Sergio Doria): "nosotros
// daremos de alta/cierre los almacenes y los depósitos para cada obra según lo consideremos" · Almacén Central para
// cualquier obra · «Almacén de obra» dentro del central con el material reservado, que sale solo con aprobación del
// Jefe de Compras y del Jefe de Logística · y en el depósito de obra "todos los ítems con sus cantidades ... y en la
// última columna el consumo del día, lo que actualiza la cantidad sobrante al terminar de procesar el consumo".
import { useState, useEffect, useCallback, useMemo } from "react";
import axios from "axios";
import { toast } from "sonner";
import { Warehouse, Plus, Loader2, Lock, Unlock, ArrowRightLeft, CheckCircle2, XCircle, ClipboardList, Search, Clock } from "lucide-react";
import { API, useAuth } from "../../contexts/AuthContext";
import { roleAllowed } from "../../utils/roles";
import SelectBuscable, { coincide } from "../../components/SelectBuscable";
import DepositoObraPanel from "./DepositoObraPanel";   // Fix II: el depósito en obra por origen

const opts = { withCredentials: true };
const inputCls = "w-full border border-gray-300 rounded-xl px-3 py-2.5 text-sm focus:ring-2 focus:ring-[#2E6DA4]/20 focus:border-[#2E6DA4] bg-white";
const labelCls = "block text-xs font-semibold text-gray-500 uppercase tracking-wide mb-1.5";
const TIPOS_ABRIR = [
  // Fix II (2026-10-30): "se crea un perfil de una «OBRA X», que está compuesta por «Almacén de OBRA X» (físicamente en
  // Dep Central) y «Depósito en OBRA X» (físicamente en la obra)" (CEN).
  { id: "obra_perfil", label: "Obra (Almacén de obra + Depósito en obra)", ayuda: "Abre los dos: el Almacén de obra, físicamente en el Depósito Central, y el Depósito en la obra. Si la obra ya tiene uno, abre solo el que falta. Si es una obra rápida que no está en la lista, escribí su nombre: le llegan las entregas de sus pedidos de obra rápida." },
  { id: "almacen_obra", label: "Almacén de obra", ayuda: "Dentro del Almacén Central, con el material reservado para la obra." },
  { id: "obra_deposito", label: "Depósito de obra", ayuda: "En la obra. Ahí se registra el consumo del día." },
  { id: "excedentes", label: "Excedentes", ayuda: "Sin obra." },
];
const TIPOS_CONSUMO = ["obra_deposito", "obrador"];
const ESTADO_TRF = {
  pendiente: { label: "Pendiente", cls: "bg-amber-50 text-amber-700 border-amber-200" },
  ejecutando: { label: "Ejecutando", cls: "bg-blue-50 text-blue-700 border-blue-200" },
  ejecutada: { label: "Aprobada y ejecutada", cls: "bg-emerald-50 text-emerald-700 border-emerald-200" },
  rechazada: { label: "Rechazada", cls: "bg-red-50 text-red-600 border-red-200" },
};
const err = (e, def) => e?.response?.data?.detail || def;
const num = (v) => Number(v || 0).toLocaleString("es-PY", { maximumFractionDigits: 4 });

// ─── Apertura / cierre ───────────────────────────────────────────────────────
function AlmacenesSeccion({ onRefresh }) {
  const [data, setData] = useState(null);
  const [verCerrados, setVerCerrados] = useState(false);
  const [q, setQ] = useState("");
  const [form, setForm] = useState(null);
  const [busy, setBusy] = useState("");
  const [confirmarVacios, setConfirmarVacios] = useState(false);   // Fix IG
  // Fix II (2026-10-30): "si puede ser retroactivo con las entregas ya realizadas y las provisiones desde depósito central
  // ya entregadas" (CEN). Vista previa y carga a pedido del Superadministrador; repetirlo no duplica.
  const { user } = useAuth();
  const esSuper = user?.role === "Superadministrador";
  const [retro, setRetro] = useState(null);
  const verRetro = async () => {
    setBusy("retro");
    try { const { data: r } = await axios.get(`${API}/inventory/depositos/retroactivo`, opts); setRetro(r); }
    catch (e) { toast.error(err(e, "No se pudo armar la vista previa")); }
    finally { setBusy(""); }
  };
  const cargarRetro = async () => {
    setBusy("retro-cargar");
    try {
      const { data: r } = await axios.post(`${API}/inventory/depositos/retroactivo`, {}, opts);
      toast.success(`Cargado en los depósitos: ${r.cargadas} línea(s)`);
      await verRetro(); onRefresh?.();
    } catch (e) { toast.error(err(e, "No se pudo cargar")); }
    finally { setBusy(""); }
  };
  const cargar = useCallback(async () => {
    try { const { data } = await axios.get(`${API}/inventory/almacenes-gestion`, opts); setData(data); }
    catch (e) { toast.error(err(e, "No se pudieron cargar los almacenes")); }
  }, []);
  useEffect(() => { cargar(); }, [cargar]);
  const lista = useMemo(() => (data?.almacenes || [])
    .filter(a => verCerrados || a.activo)
    .filter(a => !q.trim() || coincide(`${a.nombre} ${a.codigo} ${a.obra_nombre || ""} ${a.tipo_label}`, q)), [data, verCerrados, q]);
  const accion = async (a, que) => {
    setBusy(a.id + que);
    try {
      await axios.put(`${API}/inventory/almacenes/${encodeURIComponent(a.id)}/${que}`, {}, opts);
      toast.success(que === "cerrar" ? `${a.nombre}: cerrado` : `${a.nombre}: abierto de nuevo`);
      await cargar(); onRefresh?.();
    } catch (e) { toast.error(err(e, "No se pudo completar")); }
    finally { setBusy(""); }
  };
  // Fix IG (2026-10-29): los Depósitos y Obradores que se crearon solos antes del Fix IC, vacíos, se cierran de una vez.
  const vacios = (data?.almacenes || []).filter(a => a.activo && TIPOS_CONSUMO.includes(a.tipo) && !a.materiales_con_stock && !a.transferencias_pendientes);
  const cerrarVacios = async () => {
    setBusy("vacios");
    try {
      const { data: r } = await axios.post(`${API}/inventory/almacenes/cerrar-vacios`, {}, opts);
      toast.success(`${r.cerrados} cerrado(s)${r.omitidos ? ` · ${r.omitidos} con stock o transferencias quedaron abiertos` : ""}`);
      setConfirmarVacios(false); await cargar(); onRefresh?.();
    } catch (e) { toast.error(err(e, "No se pudieron cerrar")); }
    finally { setBusy(""); }
  };
  const abrir = async (e) => {
    e.preventDefault();
    // Fix IL (2026-10-30): "Al dar la apertura, que quede la opción de escribir un nombre nuevo también. Para los casos donde
    // son obras rápidas es útil esto" (CEN). Sin obra de la lista, el nombre escrito abre el perfil de una obra rápida.
    const rapida = form.tipo === "obra_perfil" && !form.obra_id ? (form.nombre || "").trim() : "";
    if (form.tipo !== "excedentes" && !form.obra_id && !rapida) { toast.error(form.tipo === "obra_perfil" ? "Elegí la obra o escribí un nombre nuevo" : "Elegí la obra"); return; }
    if (form.tipo === "obra_perfil") {   // Fix II: el perfil de la obra abre los dos que falten
      const clave = (x) => (x || "").normalize("NFD").replace(/[\u0300-\u036f]/g, "").toLowerCase().replace(/\s+/g, " ").trim();
      const ya = (data?.almacenes || []).filter(a => a.activo && (rapida ? clave(a.obra_rapida_nombre) === clave(rapida) : a.obra_id === form.obra_id)).map(a => a.tipo);
      const faltan = ["almacen_obra", "obra_deposito"].filter(t => !ya.includes(t));
      if (!faltan.length) { toast.error(`Esa obra${rapida ? " rápida" : ""} ya tiene Almacén de obra y Depósito en obra abiertos`); return; }
      setBusy("abrir");
      try {
        const nombres = [];
        for (const t of faltan) {
          const { data: r } = await axios.post(`${API}/inventory/almacenes/abrir`, { tipo: t, obra_id: form.obra_id, nombre: "", obra_rapida_nombre: rapida }, opts);
          nombres.push(r.almacen.nombre);
        }
        toast.success(`Abierto: ${nombres.join(" y ")}`);
        setForm(null); await cargar(); onRefresh?.();
      } catch (e2) { toast.error(err(e2, "No se pudo abrir")); await cargar(); }
      finally { setBusy(""); }
      return;
    }
    setBusy("abrir");
    try {
      const { data: r } = await axios.post(`${API}/inventory/almacenes/abrir`, form, opts);
      toast.success(r.reabierto ? `${r.almacen.nombre}: abierto de nuevo` : `${r.almacen.nombre}: abierto`);
      setForm(null); await cargar(); onRefresh?.();
    } catch (e2) { toast.error(err(e2, "No se pudo abrir")); }
    finally { setBusy(""); }
  };
  if (!data) return <div className="flex items-center gap-2 text-sm text-gray-500 p-6"><Loader2 size={15} className="animate-spin" /> Cargando almacenes...</div>;
  const obrasOpts = (data.obras || []).map(o => ({ value: o.id, label: `${o.nombre}${o.codigo ? ` (${o.codigo})` : ""}` }));
  return (
    <div className="space-y-4" data-testid="almacenes-seccion">
      <div className="text-xs text-gray-600 bg-[#EBF2F9] border border-[#2E6DA4]/20 rounded-xl px-4 py-3 space-y-1">
        <p><b>Almacén Central:</b> de acá se previsiona para cualquier obra.</p>
        <p><b>Almacén de obra:</b> dentro del Almacén Central, con el material reservado para esa obra. Para sacar material de ahí hace falta la aprobación del Jefe de Compras y del Jefe de Logística (pestaña Transferencias).</p>
        <p><b>Depósito de obra:</b> en la obra; ahí se registra el consumo del día. Las obras nuevas ya no crean Depósito ni Obrador solos: se abren acá.</p>
      </div>
      <div className="flex flex-wrap items-center gap-2">
        <div className="relative flex-1 min-w-[200px]">
          <Search size={14} className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" />
          <input value={q} onChange={e => setQ(e.target.value)} placeholder="Buscar almacén u obra..." className="w-full border border-gray-300 rounded-xl pl-9 pr-3 py-2.5 text-sm bg-white" data-testid="almacenes-buscar" />
        </div>
        <label className="flex items-center gap-1.5 text-xs text-gray-600"><input type="checkbox" checked={verCerrados} onChange={e => setVerCerrados(e.target.checked)} data-testid="almacenes-ver-cerrados" /> Ver cerrados</label>
        {data.puede_gestionar && (
          <button onClick={() => setForm({ tipo: "obra_perfil", obra_id: "", nombre: "" })} className="flex items-center gap-1.5 bg-[#2E6DA4] hover:bg-[#245a8a] text-white px-4 py-2.5 rounded-xl text-sm font-semibold" data-testid="almacenes-abrir-btn">
            <Plus size={15} /> Abrir almacén o depósito
          </button>
        )}
        {data.puede_gestionar && vacios.length > 0 && (
          <button onClick={() => setConfirmarVacios(true)} className="flex items-center gap-1.5 border border-gray-300 hover:bg-gray-50 text-gray-700 px-4 py-2.5 rounded-xl text-sm font-semibold" data-testid="almacenes-cerrar-vacios-btn">
            <Lock size={15} /> Cerrar vacíos ({vacios.length})
          </button>
        )}
      </div>
      {esSuper && (
        <div className="bg-white border border-gray-200 rounded-2xl p-3 space-y-2" data-testid="retro-seccion">
          <div className="flex flex-wrap items-center gap-2 justify-between">
            <p className="text-xs text-gray-600"><b>Entregas anteriores:</b> pasar a los depósitos en obra lo ya entregado (traslados del Central y compras a proveedores).</p>
            <button onClick={verRetro} disabled={busy === "retro"} className="flex items-center gap-1.5 border border-gray-300 hover:bg-gray-50 text-gray-700 px-3 py-2 rounded-xl text-xs font-semibold disabled:opacity-60" data-testid="retro-btn">
              {busy === "retro" ? <Loader2 size={13} className="animate-spin" /> : <Clock size={13} />} Ver qué falta cargar
            </button>
          </div>
          {retro && (
            <div className="space-y-2" data-testid="retro-panel">
              {retro.lineas === 0 ? <p className="text-xs text-emerald-700">No hay entregas anteriores pendientes de cargar.</p> : (
                <>
                  <table className="w-full text-xs">
                    <thead className="text-gray-500 uppercase"><tr><th className="text-left py-1">Obra</th><th className="text-right py-1">Stock CEN</th><th className="text-right py-1">Proveedor externo</th></tr></thead>
                    <tbody>{retro.obras.map(o => (
                      <tr key={o.deposito_id} className="border-t border-gray-100" data-testid={`retro-obra-${o.deposito_id}`}><td className="py-1">{o.obra || o.deposito_id}</td><td className="py-1 text-right">{o.stock_cen}</td><td className="py-1 text-right">{o.proveedor}</td></tr>
                    ))}</tbody>
                  </table>
                  <div className="flex justify-end gap-2">
                    <button onClick={() => setRetro(null)} className="px-3 py-2 rounded-lg border border-gray-300 bg-white text-xs font-semibold text-gray-700">Cerrar</button>
                    <button onClick={cargarRetro} disabled={busy === "retro-cargar"} className="flex items-center gap-1.5 px-3 py-2 rounded-lg bg-[#2E6DA4] hover:bg-[#245a8a] text-white text-xs font-semibold disabled:opacity-60" data-testid="retro-cargar">
                      {busy === "retro-cargar" ? <Loader2 size={13} className="animate-spin" /> : <CheckCircle2 size={13} />} Cargar {retro.lineas} línea(s)
                    </button>
                  </div>
                </>
              )}
            </div>
          )}
        </div>
      )}
      {confirmarVacios && (
        <div className="bg-amber-50 border border-amber-200 rounded-xl px-4 py-3 flex flex-wrap items-center gap-3 text-sm" data-testid="almacenes-cerrar-vacios-confirmar">
          <span className="flex-1 min-w-[240px] text-amber-900">Se van a cerrar {vacios.length} Depósito(s) y Obrador(es) abiertos sin stock ni transferencias pendientes. Se pueden reabrir desde «Ver cerrados».</span>
          <button onClick={() => setConfirmarVacios(false)} className="px-3 py-2 rounded-lg border border-gray-300 bg-white text-gray-700 font-semibold" data-testid="almacenes-cerrar-vacios-cancelar">Cancelar</button>
          <button onClick={cerrarVacios} disabled={busy === "vacios"} className="flex items-center gap-1.5 px-3 py-2 rounded-lg bg-amber-600 hover:bg-amber-700 text-white font-semibold disabled:opacity-60" data-testid="almacenes-cerrar-vacios-ok">
            {busy === "vacios" ? <Loader2 size={14} className="animate-spin" /> : <Lock size={14} />} Cerrar {vacios.length}
          </button>
        </div>
      )}
      {form && (
        <form onSubmit={abrir} className="bg-white border border-gray-200 rounded-2xl p-4 grid grid-cols-1 md:grid-cols-4 gap-3 items-end" data-testid="almacenes-form">
          <div><label className={labelCls}>Tipo</label>
            <select value={form.tipo} onChange={e => setForm(f => ({ ...f, tipo: e.target.value }))} className={inputCls} data-testid="almacenes-form-tipo">
              {TIPOS_ABRIR.map(t => <option key={t.id} value={t.id}>{t.label}</option>)}
            </select>
          </div>
          {form.tipo !== "excedentes" ? (
            <div className="md:col-span-2"><label className={labelCls}>Obra</label>
              <SelectBuscable value={form.obra_id} onChange={v => setForm(f => ({ ...f, obra_id: v, nombre: f.tipo === "obra_perfil" && v ? "" : f.nombre }))} options={obrasOpts}
                vacio="Elegí la obra..." placeholderBuscar="Buscar obra..." className={inputCls} testId="almacenes-form-obra" />
            </div>
          ) : <div className="md:col-span-2" />}
          <div><label className={labelCls}>{form.tipo === "obra_perfil" ? "O nombre nuevo (obra rápida)" : "Nombre (opcional)"}</label>{/* Fix IL */}
            <input value={form.nombre} onChange={e => { const v = e.target.value; setForm(f => ({ ...f, nombre: v, obra_id: f.tipo === "obra_perfil" && v.trim() ? "" : f.obra_id })); }}
              placeholder={form.tipo === "obra_perfil" ? "Si no está en la lista" : "Se arma solo"} className={inputCls} data-testid="almacenes-form-nombre" />
          </div>
          <p className="md:col-span-3 text-xs text-gray-500">{TIPOS_ABRIR.find(t => t.id === form.tipo)?.ayuda}</p>
          <div className="flex gap-2 justify-end">
            <button type="button" onClick={() => setForm(null)} className="px-3 py-2 rounded-xl border border-gray-300 text-sm">Cancelar</button>
            <button type="submit" disabled={busy === "abrir"} className="px-4 py-2 rounded-xl bg-emerald-600 hover:bg-emerald-700 text-white text-sm font-semibold disabled:opacity-60" data-testid="almacenes-form-guardar">
              {busy === "abrir" ? "Abriendo..." : "Abrir"}
            </button>
          </div>
        </form>
      )}
      <div className="bg-white border border-gray-200 rounded-2xl overflow-x-auto">
        <table className="w-full text-sm" data-testid="almacenes-tabla">
          <thead className="bg-gray-50 text-xs text-gray-500 uppercase">
            <tr><th className="text-left px-4 py-2.5">Almacén</th><th className="text-left px-4 py-2.5">Tipo</th><th className="text-left px-4 py-2.5">Obra</th>
              <th className="text-right px-4 py-2.5">Materiales con stock</th><th className="text-left px-4 py-2.5">Estado</th><th className="px-4 py-2.5" /></tr>
          </thead>
          <tbody>
            {lista.map(a => (
              <tr key={a.id} className="border-t border-gray-100" data-testid={`almacen-fila-${a.id}`}>
                <td className="px-4 py-2.5"><p className="font-semibold text-gray-900">{a.nombre}</p><p className="text-xs text-gray-400">{a.codigo}</p></td>
                <td className="px-4 py-2.5 text-gray-700">{a.tipo_label}</td>
                <td className="px-4 py-2.5 text-gray-700">{a.obra_nombre || "—"}{a.obra_rapida_nombre ? <span className="block text-[11px] text-amber-700">Obra rápida</span> : null}</td>
                <td className="px-4 py-2.5 text-right tabular-nums">{a.materiales_con_stock}{a.transferencias_pendientes ? <span className="block text-[11px] text-amber-700">{a.transferencias_pendientes} transferencia(s) pendiente(s)</span> : null}</td>
                <td className="px-4 py-2.5"><span className={`text-xs px-2 py-0.5 rounded-full border font-semibold ${a.activo ? "bg-emerald-50 text-emerald-700 border-emerald-200" : "bg-gray-100 text-gray-500 border-gray-200"}`}>{a.activo ? "Abierto" : "Cerrado"}</span></td>
                <td className="px-4 py-2.5 text-right whitespace-nowrap">
                  {data.puede_gestionar && a.tipo !== "central" && (a.activo ? (
                    <button onClick={() => accion(a, "cerrar")} disabled={!!busy} title={a.materiales_con_stock ? "Tiene stock: transferilo o consumilo antes de cerrarlo" : "Cerrar"}
                      className="inline-flex items-center gap-1 text-xs font-semibold text-red-600 border border-red-200 hover:bg-red-50 px-2.5 py-1.5 rounded-lg disabled:opacity-50" data-testid={`almacen-cerrar-${a.id}`}>
                      {busy === a.id + "cerrar" ? <Loader2 size={12} className="animate-spin" /> : <Lock size={12} />} Cerrar
                    </button>
                  ) : (
                    <button onClick={() => accion(a, "reabrir")} disabled={!!busy}
                      className="inline-flex items-center gap-1 text-xs font-semibold text-[#2E6DA4] border border-[#2E6DA4]/30 hover:bg-[#EBF2F9] px-2.5 py-1.5 rounded-lg disabled:opacity-50" data-testid={`almacen-reabrir-${a.id}`}>
                      {busy === a.id + "reabrir" ? <Loader2 size={12} className="animate-spin" /> : <Unlock size={12} />} Reabrir
                    </button>
                  ))}
                </td>
              </tr>
            ))}
            {lista.length === 0 && <tr><td colSpan={6} className="px-4 py-8 text-center text-gray-400 text-sm">Sin almacenes para mostrar</td></tr>}
          </tbody>
        </table>
      </div>
    </div>
  );
}

// ─── Transferencias ───────────────────────────────────────────────────────────
function TransferenciasSeccion({ materials, onRefresh }) {
  const { user } = useAuth();
  const [almacenes, setAlmacenes] = useState([]);
  const [lista, setLista] = useState([]);
  const [filtro, setFiltro] = useState("pendiente");
  const [form, setForm] = useState({ material_id: "", from_location: "", to_location: "", quantity: "", notes: "" });
  const [busy, setBusy] = useState("");
  const [rechazo, setRechazo] = useState({});
  const cargar = useCallback(async () => {
    try {
      const [a, t] = await Promise.all([
        axios.get(`${API}/inventory/almacenes`, opts),
        axios.get(`${API}/inventory/transferencias`, { ...opts, params: filtro ? { estado: filtro } : {} }),
      ]);
      setAlmacenes(a.data || []); setLista(t.data || []);
    } catch (e) { toast.error(err(e, "No se pudieron cargar las transferencias")); }
  }, [filtro]);
  useEffect(() => { cargar(); }, [cargar]);
  const tipoDe = id => almacenes.find(a => a.id === id)?.tipo;
  const material = (materials || []).find(m => m.id === form.material_id);
  const origenes = (material?.locations || []).filter(l => l.quantity > 0);
  const hay = origenes.find(l => l.location_id === form.from_location)?.quantity || 0;
  const conAprobacion = tipoDe(form.from_location) === "almacen_obra";
  const rol = user?.role;
  const puedeFirmar = (t, k) => !t.aprobaciones?.[k] && (roleAllowed(rol, ["Superadministrador"]) || roleAllowed(rol, [k === "compras" ? "Jefe de Compras" : "Jefe de Logística"]));
  const puedeRechazar = roleAllowed(rol, ["Superadministrador", "Jefe de Compras", "Jefe de Logística"]);
  const enviar = async (e) => {
    e.preventDefault();
    const qn = Number(form.quantity);
    if (!form.material_id || !form.from_location || !form.to_location || !(qn > 0)) { toast.error("Completá material, origen, destino y cantidad"); return; }
    if (form.from_location === form.to_location) { toast.error("El origen y el destino no pueden ser el mismo"); return; }
    if (qn > hay) { toast.error(`Stock insuficiente. Disponible: ${hay}`); return; }
    setBusy("enviar");
    try {
      if (conAprobacion) {
        const { data } = await axios.post(`${API}/inventory/transferencias`, { ...form, quantity: qn }, opts);
        toast.success(`Transferencia ${data.numero} pedida: falta la aprobación del Jefe de Compras y del Jefe de Logística`);
      } else {
        const orig = origenes.find(l => l.location_id === form.from_location);
        const dest = almacenes.find(a => a.id === form.to_location);
        await axios.post(`${API}/inventory/movements/transfer`, {
          material_id: form.material_id, quantity: qn, from_location: form.from_location, from_location_name: orig?.name || form.from_location,
          to_location: form.to_location, to_location_name: dest?.nombre || form.to_location, project_id: dest?.project_id || null, notes: form.notes || "",
        }, opts);
        toast.success("Transferencia realizada");
      }
      setForm({ material_id: "", from_location: "", to_location: "", quantity: "", notes: "" });
      await cargar(); onRefresh?.();
    } catch (e2) { toast.error(err(e2, "No se pudo transferir")); }
    finally { setBusy(""); }
  };
  const aprobar = async (t) => {
    setBusy(t.id + "ok");
    try {
      const { data } = await axios.put(`${API}/inventory/transferencias/${t.id}/aprobar`, {}, opts);
      toast.success(data.estado === "ejecutada" ? `${t.numero}: aprobada y ejecutada` : `${t.numero}: tu aprobación quedó registrada`);
      await cargar(); onRefresh?.();
    } catch (e) { toast.error(err(e, "No se pudo aprobar")); }
    finally { setBusy(""); }
  };
  const rechazar = async (t) => {
    setBusy(t.id + "no");
    try {
      await axios.put(`${API}/inventory/transferencias/${t.id}/rechazar`, { motivo: rechazo[t.id] || "" }, opts);
      toast.success(`${t.numero}: rechazada`);
      setRechazo(r => ({ ...r, [t.id]: undefined })); await cargar();
    } catch (e) { toast.error(err(e, "No se pudo rechazar")); }
    finally { setBusy(""); }
  };
  const matOpts = [...(materials || [])].filter(m => (m.locations || []).some(l => l.quantity > 0))
    .sort((a, b) => String(a.code || "").localeCompare(String(b.code || ""), "es", { numeric: true }))
    .map(m => ({ value: m.id, label: `${m.code} – ${m.name}` }));
  const firma = (t, k, nombre) => {
    const f = t.aprobaciones?.[k];
    return <span className={`inline-flex items-center gap-1 text-[11px] ${f ? "text-emerald-700" : "text-gray-400"}`} data-testid={`trf-firma-${k}-${t.id}`}>
      {f ? <CheckCircle2 size={12} /> : <Clock size={12} />} {nombre}{f ? `: ${f.by_name}` : ": pendiente"}
    </span>;
  };
  return (
    <div className="space-y-4" data-testid="transferencias-seccion">
      <form onSubmit={enviar} className="bg-white border border-gray-200 rounded-2xl p-4 grid grid-cols-1 md:grid-cols-6 gap-3 items-end" data-testid="trf-form">
        <div className="md:col-span-2"><label className={labelCls}>Material</label>
          <SelectBuscable value={form.material_id} onChange={v => setForm(f => ({ ...f, material_id: v, from_location: "" }))} options={matOpts}
            vacio="Elegí el material..." placeholderBuscar="Buscar código o material..." className={inputCls} testId="trf-material" />
        </div>
        <div><label className={labelCls}>Origen</label>
          <select value={form.from_location} onChange={e => setForm(f => ({ ...f, from_location: e.target.value }))} className={inputCls} data-testid="trf-origen">
            <option value="">Elegí...</option>
            {origenes.map(l => <option key={l.location_id} value={l.location_id}>{`${l.name} (${num(l.quantity)} ${material?.unit || ""})`}</option>)}
          </select>
        </div>
        <div><label className={labelCls}>Destino</label>
          <select value={form.to_location} onChange={e => setForm(f => ({ ...f, to_location: e.target.value }))} className={inputCls} data-testid="trf-destino">
            <option value="">Elegí...</option>
            {almacenes.filter(a => a.id !== form.from_location).map(a => <option key={a.id} value={a.id}>{a.nombre}</option>)}
          </select>
        </div>
        <div><label className={labelCls}>Cantidad</label>
          <input type="number" min="0" step="any" value={form.quantity} onChange={e => setForm(f => ({ ...f, quantity: e.target.value }))} className={inputCls} data-testid="trf-cantidad" />
        </div>
        <button type="submit" disabled={busy === "enviar"} className="flex items-center justify-center gap-1.5 bg-[#2E6DA4] hover:bg-[#245a8a] text-white px-4 py-2.5 rounded-xl text-sm font-semibold disabled:opacity-60" data-testid="trf-enviar">
          <ArrowRightLeft size={15} /> {conAprobacion ? "Pedir aprobación" : "Transferir"}
        </button>
        <p className="md:col-span-6 text-xs text-gray-500" data-testid="trf-aviso">
          {conAprobacion ? "Sale de un Almacén de obra: queda pendiente hasta que aprueben el Jefe de Compras y el Jefe de Logística; recién ahí se mueve el stock."
            : "Desde el Almacén Central (u otro almacén que no sea de obra) la transferencia se hace en el momento, como en Movimientos › Transferir."}
        </p>
      </form>
      <div className="flex gap-1">
        {[["pendiente", "Pendientes"], ["", "Todas"]].map(([k, l]) => (
          <button key={k || "todas"} onClick={() => setFiltro(k)} className={`px-3 py-2 rounded-xl text-xs font-semibold border ${filtro === k ? "bg-[#2E6DA4] text-white border-[#2E6DA4]" : "bg-white text-gray-600 border-gray-300"}`} data-testid={`trf-filtro-${k || "todas"}`}>{l}</button>
        ))}
      </div>
      <div className="space-y-2" data-testid="trf-lista">
        {lista.map(t => {
          const e = ESTADO_TRF[t.estado] || ESTADO_TRF.pendiente;
          return (
            <div key={t.id} className="bg-white border border-gray-200 rounded-2xl p-4 flex flex-col md:flex-row md:items-center gap-3" data-testid={`trf-${t.id}`}>
              <div className="flex-1 min-w-0">
                <p className="text-sm font-semibold text-gray-900">{t.numero} · {t.material_code} – {t.material_name} · {num(t.quantity)} {t.unit}</p>
                <p className="text-xs text-gray-500">{t.from_location_name} → {t.to_location_name} · pidió {t.solicitado_por_name}</p>
                <div className="flex gap-3 mt-1">{firma(t, "compras", "Jefe de Compras")}{firma(t, "logistica", "Jefe de Logística")}</div>
                {t.rechazo && <p className="text-xs text-red-600 mt-1">Rechazada por {t.rechazo.by_name}{t.rechazo.motivo ? `: ${t.rechazo.motivo}` : ""}</p>}
              </div>
              <span className={`text-xs px-2.5 py-1 rounded-full border font-semibold self-start md:self-center ${e.cls}`}>{e.label}</span>
              {t.estado === "pendiente" && (
                <div className="flex flex-wrap items-center gap-2">
                  {(puedeFirmar(t, "compras") || puedeFirmar(t, "logistica")) && (
                    <button onClick={() => aprobar(t)} disabled={!!busy} className="inline-flex items-center gap-1 text-xs font-semibold text-white bg-emerald-600 hover:bg-emerald-700 px-3 py-1.5 rounded-lg disabled:opacity-50" data-testid={`trf-aprobar-${t.id}`}>
                      <CheckCircle2 size={13} /> Aprobar
                    </button>
                  )}
                  {puedeRechazar && (
                    <>
                      <input value={rechazo[t.id] || ""} onChange={ev => setRechazo(r => ({ ...r, [t.id]: ev.target.value }))} placeholder="Motivo (opcional)" className="border border-gray-300 rounded-lg px-2 py-1.5 text-xs w-40" data-testid={`trf-motivo-${t.id}`} />
                      <button onClick={() => rechazar(t)} disabled={!!busy} className="inline-flex items-center gap-1 text-xs font-semibold text-red-600 border border-red-200 hover:bg-red-50 px-3 py-1.5 rounded-lg disabled:opacity-50" data-testid={`trf-rechazar-${t.id}`}>
                        <XCircle size={13} /> Rechazar
                      </button>
                    </>
                  )}
                </div>
              )}
            </div>
          );
        })}
        {lista.length === 0 && <p className="text-sm text-gray-400 text-center py-6">{filtro === "pendiente" ? "No hay transferencias pendientes" : "Sin transferencias"}</p>}
      </div>
    </div>
  );
}

// ─── Consumo del día ──────────────────────────────────────────────────────────
function ConsumoDiaSeccion({ onRefresh }) {
  // Fix II (2026-10-30): el consumo se registra en el depósito en obra agrupado por origen (Stock CEN, Proveedor externo,
  // Caja Chica), con «Historial de consumo» abajo. Reemplaza la tabla por material del Fix IC y los bloques del Fix IG.
  const [depositos, setDepositos] = useState([]);
  const [depId, setDepId] = useState("");
  useEffect(() => {
    axios.get(`${API}/inventory/almacenes`, opts)
      .then(({ data }) => setDepositos((data || []).filter(a => TIPOS_CONSUMO.includes(a.tipo))))
      .catch(e => toast.error(err(e, "No se pudieron cargar los depósitos")));
  }, []);
  const depOpts = depositos.map(d => ({ value: d.id, label: `${d.nombre}${d.obra_nombre ? ` · ${d.obra_nombre}` : ""}` }));
  return (
    <div className="space-y-4" data-testid="consumo-dia-seccion">
      <div className="bg-white border border-gray-200 rounded-2xl p-4">
        <label className={labelCls}>Depósito en obra</label>
        <SelectBuscable value={depId} onChange={setDepId} options={depOpts} vacio="Elegí el depósito..." placeholderBuscar="Buscar depósito u obra..." className={inputCls} testId="consumo-deposito" />
      </div>
      {depId && <DepositoObraPanel depositoId={depId} onChange={onRefresh} />}
    </div>
  );
}

export default function AlmacenesDepositosTab({ materials, onRefresh }) {
  const { user } = useAuth();
  const soloConsumo = roleAllowed(user?.role, ["Residente de Obra"]);
  const [seccion, setSeccion] = useState(soloConsumo ? "consumo" : "almacenes");
  const secciones = soloConsumo ? [["consumo", "Consumo del día", ClipboardList]]
    : [["almacenes", "Almacenes y depósitos", Warehouse], ["transferencias", "Transferencias", ArrowRightLeft], ["consumo", "Consumo del día", ClipboardList]];
  return (
    <div className="space-y-4" data-testid="almacenes-depositos-tab">
      <div className="flex gap-1 flex-wrap">
        {secciones.map(([k, l, Icon]) => (
          <button key={k} onClick={() => setSeccion(k)} className={`flex items-center gap-1.5 px-3 py-2 rounded-xl text-xs font-semibold border ${seccion === k ? "bg-[#1A4B7A] text-white border-[#1A4B7A]" : "bg-white text-gray-600 border-gray-300 hover:border-[#2E6DA4]/40"}`} data-testid={`almacenes-seccion-${k}`}>
            <Icon size={14} /> {l}
          </button>
        ))}
      </div>
      {seccion === "almacenes" && <AlmacenesSeccion onRefresh={onRefresh} />}
      {seccion === "transferencias" && <TransferenciasSeccion materials={materials} onRefresh={onRefresh} />}
      {seccion === "consumo" && <ConsumoDiaSeccion onRefresh={onRefresh} />}
    </div>
  );
}
